import { MODULES } from '@/lib/constants';
import type { ModuleSettings, SimulationSettings } from '@/lib/simulationApi';

export const SIMULATION_MODULES = MODULES.map((module) => module.value);

export const DEFAULT_MODULE_ORDER = [...SIMULATION_MODULES];

export const DEFAULT_MODULE_TIMINGS: Record<string, number> = {
  reading: 30,
  listening: 20,
  writing: 30,
  speaking: 15,
};

const createDefaultModuleSettings = (): ModuleSettings => ({
  order: [1],
  titles: {},
  questions: {},
});

export const createDefaultSectionSettings = (
  modules: string[] = SIMULATION_MODULES,
): Record<string, ModuleSettings> =>
  modules.reduce<Record<string, ModuleSettings>>((settings, module) => {
    settings[module] = createDefaultModuleSettings();
    return settings;
  }, {});

export const normalizeModuleOrder = (
  moduleOrder: readonly string[] | null | undefined,
  modules: string[] = SIMULATION_MODULES,
): string[] => {
  const seen = new Set<string>();
  const normalized = (moduleOrder ?? []).filter((module) => {
    if (!modules.includes(module) || seen.has(module)) {
      return false;
    }
    seen.add(module);
    return true;
  });

  return normalized.length > 0 ? normalized : [...modules];
};

export const normalizeModuleTimings = (
  moduleTimings: Record<string, number> | null | undefined = {},
): Record<string, number> => ({
  ...DEFAULT_MODULE_TIMINGS,
  ...(moduleTimings ?? {}),
});

export const normalizeSectionSettings = (
  sectionSettings: Record<string, ModuleSettings> | null | undefined = {},
  modules: string[] = SIMULATION_MODULES,
): Record<string, ModuleSettings> => {
  const normalized = createDefaultSectionSettings(modules);

  Object.entries(sectionSettings ?? {}).forEach(([module, settings]) => {
    normalized[module] = {
      order:
        Array.isArray(settings?.order) && settings.order.length > 0
          ? settings.order
          : [1],
      titles: settings?.titles ?? {},
      questions: settings?.questions ?? {},
    };
  });

  return normalized;
};

export const calculateSimulationTotalDuration = (
  moduleOrder: readonly string[],
  moduleTimings: Record<string, number>,
  pauseBetweenModules: number,
): number => {
  const moduleTotal = moduleOrder.reduce((sum, module) => {
    const time = Number(moduleTimings[module]) || 0;
    return sum + Math.max(0, time);
  }, 0);
  const pauseTotal =
    Math.max(0, moduleOrder.length - 1) *
    Math.max(0, Number(pauseBetweenModules) || 0);

  return moduleTotal + pauseTotal;
};

export const toggleSimulationModule = (
  module: string,
  enabled: boolean,
  currentOrder: readonly string[],
  modules: string[] = SIMULATION_MODULES,
): string[] => {
  if (!modules.includes(module)) {
    return [...currentOrder];
  }

  const isCurrentlyEnabled = currentOrder.includes(module);
  if (enabled) {
    return isCurrentlyEnabled ? [...currentOrder] : [...currentOrder, module];
  }

  if (!isCurrentlyEnabled || currentOrder.length <= 1) {
    return [...currentOrder];
  }

  return currentOrder.filter((enabledModule) => enabledModule !== module);
};

export const buildDefaultSimulationSettings = (): SimulationSettings => {
  const moduleOrder = [...DEFAULT_MODULE_ORDER];
  const moduleTimings = normalizeModuleTimings();
  const pauseBetweenModules = 5;

  return {
    level: 'A1',
    examType: 'goethe',
    moduleOrder,
    moduleTimings,
    pauseBetweenModules,
    sectionSettings: createDefaultSectionSettings(),
    totalDuration: calculateSimulationTotalDuration(
      moduleOrder,
      moduleTimings,
      pauseBetweenModules,
    ),
    isActive: true,
    isDefault: false,
  };
};

export const normalizeSimulationSettings = (
  settings: SimulationSettings,
): SimulationSettings => {
  const moduleOrder = normalizeModuleOrder(settings.moduleOrder);
  const moduleTimings = normalizeModuleTimings(settings.moduleTimings);
  const sectionSettings = normalizeSectionSettings(settings.sectionSettings);

  return {
    ...settings,
    moduleOrder,
    moduleTimings,
    sectionSettings,
    totalDuration: calculateSimulationTotalDuration(
      moduleOrder,
      moduleTimings,
      settings.pauseBetweenModules,
    ),
  };
};
